import type { NumberInputProps as MantineNumberInputProps } from '@mantine/core'
import { NumberInput as MantineNumberInput } from '@mantine/core'
import { useRef, useState } from 'react'

export type NumberInputProps = Omit<MantineNumberInputProps, 'onChange'> & {
  onChange?: (value: number | '') => void
}

const toWholeNumber = (value: number) => Number(value.toFixed(0))

export const NumberInput = ({
  onChange,
  onFocus,
  onBlur,
  decimalScale,
  value,
  ...props
}: NumberInputProps) => {
  const wholeNumbers = !decimalScale && value !== undefined
  const input = useRef<HTMLInputElement>(null)
  const [draft, setDraft] = useState<number | string | null>(null)

  return (
    <MantineNumberInput
      {...props}
      ref={input}
      // Mantine 8 asks phones for the decimal keypad even on whole-number fields, where Mantine 6 used the numeric one
      inputMode={props.inputMode ?? (decimalScale ? 'decimal' : 'numeric')}
      value={draft ?? value}
      decimalScale={draft === null ? (decimalScale ?? 0) : undefined}
      onFocus={(event) => {
        if (wholeNumbers) {
          setDraft((current) => current ?? (typeof value === 'number' ? toWholeNumber(value) : (value ?? '')))
        }

        onFocus?.(event)
      }}
      onBlur={(event) => {
        setDraft(null)

        onBlur?.(event)
      }}
      onChange={(changed) => {
        // Mantine 8 reports partial input as a string, so a negative number starts as a lone '-'.
        // Every handler expects a number or '', so it is held back until it parses.
        const numberValue = changed === '' ? '' : Number(changed)

        if (numberValue !== '' && Number.isNaN(numberValue)) return

        const typing = wholeNumbers && document.activeElement === input.current

        if (typing) setDraft(numberValue)

        onChange?.(typing && numberValue !== '' ? toWholeNumber(numberValue) : numberValue)
      }}
    />
  )
}
